import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useGoToHome from 'lib/hooks/useGoToHome';

import { throwErrorToast, throwSuccessToast } from 'lib/utils/toast';

import { TRIGGER_WORKFLOW } from 'lib/api/mutations';
import { actionTriggerWorkflow } from 'lib/actions';

import { useTranslation } from 'react-i18next';

import { bugsnag } from 'lib/external/bugsnag';
import { getBugsnagUserInfo } from 'lib/selectors/getBugsnagUserInfo';
import { useMutation } from '@apollo/client';

const ERROR_TOAST_DURATION = 5000;

const DEFAULT_ERROR_CODE = 'ACTION_FAILED';

const getErrorMessage = (errorCode, t) => {
  return t(errorCode || DEFAULT_ERROR_CODE);
};

const useWorkflowTrigger = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const bugsnagUserInfo = useSelector(getBugsnagUserInfo);
  const goToHome = useGoToHome();

  const onError = useCallback(
    ({ networkError }) => {
      if (networkError) {
        throwErrorToast(t('Error triggering workflow'));

        bugsnag.notify(new Error('Error triggering workflow'), (event) => {
          event.addMetadata('user', bugsnagUserInfo);
        });
      }
    },
    [t, bugsnagUserInfo]
  );

  const onCompleted = useCallback(
    (data) => {
      const actionResults = data?.triggerWorkflowResponse?.actionResults;
      const failedActions = actionResults?.filter((r) => {
        return r.status === 'failed';
      });

      if (failedActions?.length > 0) {
        failedActions.forEach((result) => {
          throwErrorToast(getErrorMessage(result.errorCode, t), {
            autoClose: ERROR_TOAST_DURATION,
          });
        });
      } else {
        throwSuccessToast(t('Workflow triggered'));
      }
    },
    [t]
  );

  const [triggerWorkflow, { loading }] = useMutation(TRIGGER_WORKFLOW, {
    fetchPolicy: 'no-cache',
    onCompleted,
    onError,
  });

  const submit = useCallback(
    (workflow, machine, message = '') => {
      dispatch(actionTriggerWorkflow(workflow));

      const input = {
        machineId: machine.id,
        monitorId: workflow.id,
        machineRef: machine.machineRef,
      };

      // Add message if provided
      if (message && message.trim()) {
        input.message = message.trim();
      }

      return triggerWorkflow({
        variables: {
          input,
        },
      });
    },
    [triggerWorkflow, dispatch]
  );

  return { submit, loading, goToHome };
};

export default useWorkflowTrigger;
